/**
 * Solicitudes de catálogo (P38) · el solicitante pide al administrador dar de
 * alta un elemento que no existe en un catálogo (hoy: proveedores por RFC).
 * El administrador las recibe en su bandeja "Solicitudes de catálogos".
 */
import { axiosClient } from "@/shared/api/axios-client";
import type { Paginado } from "@/lib/types";

export type EstadoSolicitudCatalogo = "Pendiente" | "Atendida" | "Rechazada";

export interface SolicitudDeCatalogo {
  id: string;
  tipo: string;
  clave: string;
  descripcion: string | null;
  comentario: string | null;
  estado: EstadoSolicitudCatalogo;
  solicitanteId: string;
  solicitanteNombre: string | null;
  solicitudPagoId: string | null;
  numeroSolicitudPago: number | null;
  creadoAt: string;
  resueltaAt: string | null;
  respuestaComentario: string | null;
  /** null = resolución aún no vista por el solicitante (campana). */
  vistaPorSolicitanteAt: string | null;
}

export interface CrearSolicitudCatalogoInput {
  tipo: string;
  clave: string;
  descripcion?: string;
  comentario?: string;
  solicitudPagoId?: string;
  numeroSolicitudPago?: number;
}

export interface CrearSolicitudCatalogoResultado {
  solicitud: SolicitudDeCatalogo;
  /** true → ya había una pendiente para (tipo, clave) · no se duplicó. */
  yaExistia: boolean;
}

export async function crearSolicitudCatalogo(
  input: CrearSolicitudCatalogoInput
): Promise<CrearSolicitudCatalogoResultado> {
  const { data } = await axiosClient.post<CrearSolicitudCatalogoResultado>(
    "/api/catalogos/solicitudes",
    input
  );
  return data;
}

/** null cuando nadie ha pedido esa clave (el backend responde 204). */
export async function obtenerEstadoSolicitudCatalogo(
  tipo: string,
  clave: string
): Promise<SolicitudDeCatalogo | null> {
  const response = await axiosClient.get<SolicitudDeCatalogo | "">(
    `/api/catalogos/solicitudes/estado?tipo=${encodeURIComponent(tipo)}&clave=${encodeURIComponent(clave)}`
  );
  return response.status === 204 || !response.data ? null : response.data;
}

// --- Campana de notificaciones del solicitante -----------------------------------

export async function listarMisSolicitudesCatalogo(): Promise<SolicitudDeCatalogo[]> {
  const { data } = await axiosClient.get<SolicitudDeCatalogo[]>(
    "/api/catalogos/solicitudes/mias"
  );
  return data;
}

export async function contarMisResolucionesNoVistas(): Promise<number> {
  const { data } = await axiosClient.get<{ total: number }>(
    "/api/catalogos/solicitudes/mias/no-vistas/count"
  );
  return data.total;
}

export async function marcarMisSolicitudesVistas(): Promise<void> {
  await axiosClient.post("/api/catalogos/solicitudes/mias/marcar-vistas");
}

/** Elimina la notificación de MI campana (solo resueltas · el registro persiste). */
export async function eliminarMiNotificacion(id: string): Promise<void> {
  await axiosClient.delete(`/api/catalogos/solicitudes/mias/${id}`);
}

/** Elimina la solicitud de la bandeja del ADMIN (solo resueltas · soft-hide). */
export async function eliminarSolicitudCatalogoAdmin(id: string): Promise<void> {
  await axiosClient.delete(`/api/admin/solicitudes-catalogo/${id}`);
}

// --- Bandeja del administrador -------------------------------------------------

export interface ListarSolicitudesCatalogoParams {
  estado?: string;
  busqueda?: string;
  page?: number;
  pageSize?: number;
}

/** Listado PAGINADO server-side de la bandeja admin (estado + búsqueda en el server). */
export async function listarSolicitudesCatalogo(
  params: ListarSolicitudesCatalogoParams = {}
): Promise<Paginado<SolicitudDeCatalogo>> {
  const { data } = await axiosClient.get<Paginado<SolicitudDeCatalogo>>(
    "/api/admin/solicitudes-catalogo",
    { params }
  );
  return data;
}

export async function contarSolicitudesCatalogoPendientes(): Promise<number> {
  const { data } = await axiosClient.get<{ total: number }>(
    "/api/admin/solicitudes-catalogo/pendientes/count"
  );
  return data.total;
}

export async function atenderSolicitudCatalogo(
  id: string,
  comentario?: string
): Promise<SolicitudDeCatalogo> {
  const { data } = await axiosClient.post<SolicitudDeCatalogo>(
    `/api/admin/solicitudes-catalogo/${id}/atender`,
    { comentario: comentario || null }
  );
  return data;
}

export async function rechazarSolicitudCatalogo(
  id: string,
  comentario: string
): Promise<SolicitudDeCatalogo> {
  const { data } = await axiosClient.post<SolicitudDeCatalogo>(
    `/api/admin/solicitudes-catalogo/${id}/rechazar`,
    { comentario }
  );
  return data;
}
